import { Component, inject, Input, Output, EventEmitter } from '@angular/core';
import { MedicationRequest, Patient } from 'fhir/r4';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { AsyncPipe, CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { catchError, map, mergeMap, switchMap, tap, throwError } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ErrorService } from '../shared/error.service';
import { AgePipe } from "../age.pipe";
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { backendEndPointToken } from '../app.config';
import { DisableSortParam } from '../loading.interceptor';

import { authBackendEndPointToken } from '../app.config';
import { patientSignatureExtensionUrlToken, practitionerSignatureExtensionUrlToken } from '../app.config';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { ConfirmActionDialogComponent } from './confirm-action-dialog.component';
import { AuthService } from '../shared/auth/auth.service';
import { attachmentToDataUrl, getSignatureAttachment } from '../shared/signature-extension.utils';
export type PatientReg = Patient & { status: string }
@Component({
  selector: 'app-patient-registration-details',
  imports: [MatCardModule, MatButtonModule, MatDividerModule, DatePipe, CommonModule,
    TitleCasePipe, MatIconModule, AgePipe, MatTabsModule],
  templateUrl: './patient-registration-details.component.html',
  styleUrls: ['../dummy-medication-request-details/dummy-medication-request-details.component.scss', './patient-registration-details.component.scss']
})

export class PatientRegistrationDetailsComponent {
  @Input() inputData?: PatientReg
  @Input() practitionerPurpose?: boolean
  @Input() isEditModeForPractitioner: boolean = false;
  @Output() editRequested = new EventEmitter<Patient>();
  http = inject(HttpClient);
  authBackendApiBaseUrl = inject(authBackendEndPointToken)
  private dialog = inject(MatDialog); // Add MatDialog
  private auth = inject(AuthService);
  private readonly patientSignatureExtensionUrl = inject(patientSignatureExtensionUrlToken);
  private readonly practitionerSignatureExtensionUrl = inject(practitionerSignatureExtensionUrlToken);
  isViewOnly: boolean = false;
  isArchived: boolean = false;
  isDeceased: boolean = false;
  currentSegment?: string;
  user$ = this.auth.user;
  private _sn = inject(MatSnackBar);
  allMedications!: MedicationRequest[];
  statuStyles: any;
  private injectedData: any = inject(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject(MatDialogRef<PatientRegistrationDetailsComponent>, { optional: true });
  errorService = inject(ErrorService);
  canModeratePending$ = this.auth.user.pipe(
    map(() => this.auth.can('patient', 'add'))
  );

  // Add properties for the new structure
  approvalData?: any;
  fhirResource?: Patient;
  isPending: boolean = false;
  data: any = null; // This will be used by the template

  ngOnInit() {
    this.statuStyles = baseStatusStyles;

    // Handle the new data structure
    if (this.injectedData) {
      this.currentSegment = this.injectedData.currentSegment; // Get the segment FIRST
      this.isPending = this.injectedData.isPending ?? false;
      this.isViewOnly = this.injectedData.isViewOnly ?? false;

      // Determine status based on currentSegment first, then fall back to data
      if (this.currentSegment === 'archived') {
        this.isArchived = true;
        this.isDeceased = false;
        this.isPending = false; // Explicitly set to false
      } else if (this.currentSegment === 'deceased') {
        this.isDeceased = true;
        this.isArchived = false;
        this.isPending = false; // Explicitly set to false
      } else if (this.currentSegment === 'approved') {
        this.isArchived = false;
        this.isDeceased = false;
        // isPending already set above
      } else if (this.currentSegment === 'pending') {
        this.isPending = true;
        this.isArchived = false;
        this.isDeceased = false;
      }

      if (this.injectedData.fhirResource) {
        this.fhirResource = this.injectedData.fhirResource;
        this.data = this.fhirResource;

        // Only check FHIR data if segment wasn't provided
        if (!this.currentSegment) {
          this.isArchived = this.data.active === false && !this.data.deceasedBoolean && !this.data.deceasedDateTime;
          this.isDeceased = this.data.deceasedBoolean === true || !!this.data.deceasedDateTime;
        }

        if (this.isPending) {
          this.approvalData = {
            id: this.injectedData.id,
            email: this.injectedData.email,
            firstName: this.injectedData.firstName,
            lastName: this.injectedData.lastName,
            phoneNumber: this.injectedData.phoneNumber,
            accountType: this.injectedData.accountType,
            requestedRole: this.injectedData.requestedRole,
            organizationId: this.injectedData.organizationId,
            fhirResourceId: this.injectedData.fhirResourceId,
            status: this.injectedData.status,
            submittedAt: this.injectedData.submittedAt,
            fullName: this.injectedData.fullName
          };
        }
      } else {
        this.data = this.injectedData;
      }
    }
  }



  ngOnChanges() {
    if (!this.data && this.inputData) {
      this.data = this.inputData;
    }
  }


  openChangePasswordModal(): void {
    const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
      width: '550px',
      maxWidth: '90vw',
      panelClass: 'change-password-dialog',
      disableClose: false,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        console.log('Password changed successfully');
      }
    })
  }
  // renderOtherFields(data: any, startKey: string, exclude: string[]) {
  //   let otherDetailWrapperStartTag = `<div class="other-details">`;
  //   let otherDetailWrapperEndTag = `</div>`
  //   if (startKey !== null && data[startKey]) {
  //     const otherFields = Object.keys(data[startKey]).filter((key) => !exclude.includes(key));
  //     for (const field of otherFields) {
  //       console.log(field);
  //       let fieldGroupWrapper = `<div class="field-group">`;
  //       let fieldGroupWrapperEnd = `</div>`
  //       let individualDetailWrapperStart = ""
  //       while (typeof data[startKey][field] == "object") {
  //         individualDetailWrapperStart = fieldGroupWrapper + `<div class="field-group-title">${field}</div><div class="individual-detail">`
  //         let individualDetailWrapperEnd = `</div>`
  //         for (const key of Object.keys(data[startKey][field])) {

  //         }

  //       }
  //       let finalGroup = `
  //       <div class="field-group">
  //                                       <div class="field-value" [innerHTML]="instruction.text">

  //                                       </div>
  //                                       <div class="field-title">
  //                                           Dosage Insructions In Text
  //                                       </div>
  //                                   </div>`

  //       for (let i = 0; i < data[startKey][field].length; i++) {
  //         field
  //       }
  //       let individualDetailWrapperEnd = `</div>`
  //     }
  //   }

  // }

  fieldKeys(data: any, exclude: any[]) {
    return Object.keys(data).filter((e) => {
      if (exclude.includes(e)) {
        return false
      } else {
        return true
      }
    });

  }

  renderOtherFields(data: any, key: string = '', parentWrapper: any = null): any {
    console.log(parentWrapper);
    const elements: string[] = [];

    const isPrimitive = (val: any) =>
      val === null || ['string', 'number', 'boolean'].includes(typeof val);

    //  for (const key in data) {
    if (!data.hasOwnProperty(key)) {
      return '';
    };

    //const fullKey = startKey ? `${startKey}.${key}` : key;
    const value = data[key];

    if (isPrimitive(value)) {
      console.log('primitive', value);
      const el = document.createElement('div');
      el.innerHTML = `
      ${['system', 'code', 'display'].includes(key.split('.')[key.split('.').length - 1]) ? "" :
          ` ${key.split('.')[key.split('.').length - 1]} - `
        }${value}`;
      //       el.innerHTML =     ` <div class="field-group">
      //                                         <div class="field-value">
      // ${value}
      //                                         </div>
      //                                         <div class="field-title">
      //                                            ${key.split('.')[key.split('.').length - 1]}
      //                                         </div>
      //                                     </div>`;
      console.log(el.innerHTML);

      // <strong>: </strong> ${value}`;

      if (!parentWrapper) {
        console.log(el.innerHTML)
        if (['system', 'code'].includes(key.split('.')[key.split('.').length - 1])) {
          return "";
        }
        return el.innerHTML
      } else {
        if (['system', 'code'].includes(key.split('.')[key.split('.').length - 1])) {
          return parentWrapper.innerHTML;
        }
        console.log(parentWrapper.innerHTML);
        if ((parentWrapper as HTMLElement).children.length) {
          (parentWrapper as HTMLElement).children[(parentWrapper as HTMLElement).children.length - 1].appendChild(el);
        } else {
          parentWrapper.appendChild(el);
        }
        return parentWrapper.innerHTML
      }


      //elements.push(el.innerHTML);

    } else {
      if (value instanceof Array) {
// $#$        // /* alert("Array"); */
        if (!parentWrapper) {
          parentWrapper = document.createElement('div');
          parentWrapper.classList.add('detail-group');
        }
        for (const k of Object.keys(value[0])) {
          this.renderOtherFields(value[0], k, parentWrapper);
        }
        //  this.renderOtherFields(value, '0', parentWrapper);
      } else {
        console.log('object', value)
        if (parentWrapper) {
          let childWrapper = document.createElement('div');
          childWrapper.classList.add('detail-group');
          childWrapper.innerHTML = `<div class="detail-group-title"> ${key.split('.')[key.split('.').length - 1]}  </div>`;
          if (['system', 'Coding', 'code'].includes(key.split('.')[key.split('.').length - 1])) {
            parentWrapper.appendChild(childWrapper);
          }

          console.log(parentWrapper);
        } else {
          parentWrapper = document.createElement('div');
          parentWrapper.classList.add('detail-group');
          if (['system', 'Coding', 'code'].includes(key.split('.')[key.split('.').length - 1])) {
            parentWrapper.innerHTML = `<div class="detail-group-title"> ${key.split('.')[key.split('.').length - 1]}  </div>`;
          }

        }
        for (const k of Object.keys(value)) {
          this.renderOtherFields(value, k, parentWrapper);
        }
      }
      //this.renderOtherFields(value, `$key, parentWrapper);
    }
    // } else {
    //   console.log('val', typeof (value));
    // }
    // }
    //console.log(hugeWrapper);
    // for (const e of elements) {
    //   document.querySelector(".other-details")!.innerHTML += e;
    // }
    console.log(parentWrapper);
    return parentWrapper.innerHTML
    // ? parentWrapper.innerHTML : '';

  }
  // Approve: fetch original Patient then PUT with active=true


  // Add reactivate method
  reactivatePatient() {
    const fhirId = this.data?.id || this.fhirResource?.id;
    if (!fhirId) {
      this.errorService.openandCloseError('Missing patient ID.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '450px',
      data: {
        title: 'Reactivate Patient',
        message: 'Are you sure you want to reactivate this patient? This will restore their account and access.',
        confirmText: 'Reactivate',
        confirmColor: 'primary',
        isInput: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed) {
        const url = `${this.authBackendApiBaseUrl}/api/admin/patients/${fhirId}/reactivate`;

        this.http.post(url, {}).subscribe({
          next: () => {
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient reactivated successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
            this.dialogRef?.close({ reactivated: true, id: fhirId });
          },
          error: (err) => {
            console.error('Reactivation failed', err);
            this.errorService.openandCloseError('Could not reactivate patient. Please try again.');
          }
        });
      }
    });
  }

  // Add delete method
  deletePatient() {
    const id = this.data?.id || this.fhirResource?.id;
    if (!id) {
      this.errorService.openandCloseError('Missing patient ID.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '450px',
      data: {
        title: 'Delete Patient',
        message: 'Are you sure you want to permanently delete this patient? This action cannot be undone and will remove all patient data.',
        confirmText: 'Delete Permanently',
        confirmColor: 'warn',
        isInput: true,
        inputLabel: 'Type "DELETE" to confirm'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed && result.value === 'DELETE') {
        const url = `${this.authBackendApiBaseUrl}/api/admin/patients/${id}`;

        this.http.delete(url).subscribe({
          next: () => {
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient deleted successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
            this.dialogRef?.close({ deleted: true, id });
          },
          error: (err) => {
            console.error('Deletion failed', err);
            this.errorService.openandCloseError(err.error?.message || 'Could not delete patient.');
          }
        });
      } else if (result && result.confirmed) {
        this.errorService.openandCloseError('Confirmation text did not match. Patient not deleted.');
      }
    });
  }

  // Add edit method
  editPatient() {
    if (this.dialogRef) {
      this.dialogRef.close({ edit: true, resource: this.data });
      return;
    }

    if (this.data) {
      this.editRequested.emit(this.data);
    }
  }

  // Add archive method
  archivePatient() {
    const id = this.data?.id || this.fhirResource?.id;
    if (!id) {
      this.errorService.openandCloseError('Missing patient ID.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '450px',
      data: {
        title: 'Archive Patient',
        message: 'Are you sure you want to archive this patient? The account will be deactivated but data will be preserved.',
        confirmText: 'Archive',
        confirmColor: 'accent',
        isInput: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed) {
        const url = `${this.authBackendApiBaseUrl}/api/admin/patients/${id}/archive`;

        this.http.post(url, { reason: 'Archived by administrator' }).subscribe({
          next: () => {
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient archived successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
            this.dialogRef?.close({ archived: true, id });
          },
          error: (err) => {
            console.error('Archive failed', err);
            this.errorService.openandCloseError('Could not archive patient. Please try again.');
          }
        });
      }
    });
  }

  approvePatient() {
    const id = this.approvalData?.id || this.data?.id;
    if (!id) {
      this.errorService.openandCloseError('Missing approval id.');
      return;
    }

    // 1. Open Confirmation Dialog
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '450px',
      data: {
        title: 'Approve Registration',
        message: 'Are you sure you want to approve this patient? This will create their account and send a welcome email.',
        confirmText: 'Approve',
        confirmColor: 'primary',
        isInput: false
      }
    });

    // 2. Handle Dialog Result
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed) {
        // 3. Proceed with API Call
        const url = `${this.authBackendApiBaseUrl}/api/Admin/approvals/${id}/approve`;
            const body = {
          roles: ['patient']
        };

        this.http.post(url, body).subscribe({
          next: (res) => {
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient registration approved successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
            this.dialogRef?.close({ approved: true, id, resource: res });
          },
          error: (err) => {
            console.error('Admin approval failed', err);
            this.errorService.openandCloseError('Could not approve registration. Please try again.');
          }
        });
      }
    });
  }

isRejecting: boolean = false;

rejectPatient() {
  const id = this.approvalData?.id || this.data?.id;
  if (!id) {
    this.errorService.openandCloseError('Missing approval id.');
    return;
  }

  const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
    width: '450px',
    data: {
      title: 'Reject Registration',
      message: 'Please provide a reason for rejecting this application. This reason will be emailed to the user.',
      confirmText: 'Reject Application',
      confirmColor: 'warn',
      isInput: true,
      inputLabel: 'Rejection Reason'
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result && result.confirmed && !this.isRejecting) {  // ← guard here
      this.isRejecting = true;                              // ← lock

      const reason = result.value || "Application did not meet requirements.";
      const url = `${this.authBackendApiBaseUrl}/api/Admin/approvals/${id}/reject`;

      this.http.post(url, { reason }).subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Patient registration rejected.', action: 'Close' },
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          this.isRejecting = false;
          this.dialogRef?.close({ rejected: true, id });
        },
        error: (err) => {
          this.isRejecting = false;  // ← unlock on error
          console.error('patient rejection failed', err);
          this.errorService.openandCloseError('Could not reject registration. Please try again.');
        }
      });
    }
  });
}

  getProfileImageUrl(): string {
    const photo = this.data?.photo?.[0];
    if (photo) {
      if (photo.url) {
        return photo.url;
      }
      if (photo.data && photo.contentType) {
        return `data:${photo.contentType};base64,${photo.data}`;
      }
    }

    const currentUserPhoto = this.auth.user.getValue()?.photo?.[0]?.url;
    if (currentUserPhoto) {
      return currentUserPhoto;
    }

    return 'https://www.dummyimg.in/placeholder';
  }

  get signatureImageUrl(): string | null {
    if (!this.data) {
      return null;
    }
    const extensionUrl = this.data?.resourceType === 'Practitioner'
      ? this.practitionerSignatureExtensionUrl
      : this.patientSignatureExtensionUrl;
    const attachment = getSignatureAttachment(this.data, extensionUrl);
    return attachmentToDataUrl(attachment);
  }
}
